import React from 'react';
import { Box, Grid, ButtonBase, Typography, useTheme } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useTranslation } from 'react-i18next';

import { useSettingsContext } from '../../../../setting/SettingsContext';
import { FONTS, FONTS_LIST } from '../../../../setting/fonts';
import SectionLabel from './SectionLabel';

/**
 * Font-family picker. Each card previews "Aa" rendered in its own font
 * stack so the operator sees what the body text will look like before
 * committing.
 *
 * Click → onChangeFontFamily → SettingsProvider rebuilds the MUI theme
 * with `typography.fontFamily` set to the picked stack. All MUI Text
 * components and the global CssBaseline body inherit from this stack.
 */
const FontOptions = () => {
    const theme = useTheme();
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { themeFontFamily, onChangeFontFamily } = useSettingsContext();

    return (
        <Box>
            <SectionLabel>{isArabic ? 'الخط' : 'Font'}</SectionLabel>
            <Grid container spacing={1.25}>
                {FONTS_LIST.map((key) => {
                    const font = FONTS[key];
                    const selected = themeFontFamily === key;
                    return (
                        <Grid item xs={6} key={key}>
                            <ButtonBase
                                onClick={() => onChangeFontFamily(key)}
                                aria-label={`${font.name} font`}
                                sx={{
                                    width: '100%',
                                    minHeight: 76,
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    p: 1.25,
                                    borderRadius: 2,
                                    border: `1.5px solid ${
                                        selected ? theme.palette.primary.main : theme.palette.divider
                                    }`,
                                    bgcolor: selected
                                        ? `${theme.palette.primary.main}10`
                                        : 'transparent',
                                    color: selected ? theme.palette.primary.main : 'text.primary',
                                    transition: 'all 0.2s',
                                    position: 'relative',
                                    '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        bgcolor: `${theme.palette.primary.main}08`,
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: font.stack,
                                        fontSize: 22,
                                        fontWeight: 700,
                                        lineHeight: 1,
                                    }}
                                >
                                    Aa
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontFamily: font.stack,
                                        fontWeight: 600,
                                        textAlign: 'center',
                                    }}
                                >
                                    {font.name}
                                </Typography>
                                {selected && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 4,
                                            insetInlineEnd: 4,
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            bgcolor: theme.palette.primary.main,
                                            color: theme.palette.primary.contrastText,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <CheckRoundedIcon sx={{ fontSize: 12 }} />
                                    </Box>
                                )}
                            </ButtonBase>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};

export default FontOptions;
